import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { formatDate, formatNumber, getStatusBadgeClass, capitalize, debounce } from '../utils/helpers';
import {
  CreditCard,
  DollarSign,
  Users,
  TrendingUp,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Search
} from 'lucide-react';

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    dateRange: 'all',
    billingCycle: 'all'
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');

  const fetchSubscriptions = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      // Build params object for pagination
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      // Add search filter if provided
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.billingCycle && filters.billingCycle !== 'all') {
        params.append('billingCycle', filters.billingCycle);
      }
      if (filters.dateRange && filters.dateRange !== 'all') {
        if (filters.dateRange === 'custom') {
          if (customDateRange.startDate) {
            params.append('startDate', customDateRange.startDate);
          }
          if (customDateRange.endDate) {
            params.append('endDate', customDateRange.endDate);
          }
        } else {
          params.append('dateRange', filters.dateRange);
        }
      }

      // Use the dedicated admin subscriptions endpoint with pagination
      const response = await api.get(`/admin/subscriptions?${params}`);
      const { subscriptions: subscriptionData, stats, pagination: paginationData } = response.data;

      // Transform subscription data for display
      const transformedData = subscriptionData.map(user => ({
        id: user._id,
        user: {
          name: user.name,
          email: user.email
        },
        plan: user.plan,
        status: user.subscription?.status || (user.isActive ? 'active' : 'cancelled'),
        createdAt: user.createdAt,
        nextBilling: user.subscription?.nextPaymentDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        amount: user.plan == 'free' ? 0 : getPlanAmount(user.plan, user.subscription?.billingCycle) || 0,
        currency: user.subscription?.currency || 'USD',
        provider: user.subscription?.provider || 'lemonsqueezy',
        billingCycle: user.subscription?.billingCycle || '',
      }));

      setSubscriptions(transformedData);
      
      // Transform pagination data to match expected format
      const transformedPagination = {
        currentPage: paginationData?.page || 1,
        totalPages: paginationData?.pages || 1,
        totalSubscriptions: paginationData?.total || 0,
        hasPrevPage: (paginationData?.page || 1) > 1,
        hasNextPage: (paginationData?.page || 1) < (paginationData?.pages || 1)
      };
      
      setPagination(transformedPagination);
      setStats(stats);

    } catch (error) {
      console.error('Fetch subscriptions error:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
    fetchSubscriptions(1);
  }, [fetchSubscriptions]);

  const getPlanAmount = (plan, type = 'monthly') => {
    var amounts = {};
    if (type == 'monthly') {
      amounts = {
        free: 0,
        pro: 5,
        // premium: 39.99,
        guest: 0
      }
    } else {
      amounts = {
        free: 0,        basic: 0,

        basic: 0,
        pro: 25,
        // premium: 39.99,
        guest: 0
      }
    }
    return amounts[plan] || 0;
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const getPlanColor = (plan) => {
    const colors = {
      free: 'text-gray-600',
      basic: 'text-green-600',
      pro: 'text-blue-600',
      premium: 'text-purple-600',
      guest: 'text-gray-400'
    };
    return colors[plan] || 'text-gray-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'past_due':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <p className="mt-1 text-sm text-gray-600">
            Monitor subscription plans and billing information
          </p>
        </div>
      </div>

      {/* Search Filter */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email..."
                className="input pl-10"
                value={searchValue}
                onChange={handleSearchChange}
              />
            </div>
            
            <select
              className="input"
              value={filters.billingCycle}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, billingCycle: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="all">All Billing Cycles</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            
            <select
              className="input"
              value={filters.dateRange}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, dateRange: e.target.value }));
                if (e.target.value !== 'custom') {
                  setCustomDateRange({ startDate: '', endDate: '' });
                }
                setCurrentPage(1);
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {filters.dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  className="input"
                  value={customDateRange.startDate}
                  onChange={(e) => {
                    setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }));
                    setCurrentPage(1);
                  }}
                  placeholder="Start Date"
                  max={customDateRange.endDate || undefined}
                />
                <input
                  type="date"
                  className="input"
                  value={customDateRange.endDate}
                  onChange={(e) => {
                    setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }));
                    setCurrentPage(1);
                  }}
                  placeholder="End Date"
                  min={customDateRange.startDate || undefined}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.totalSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.activeSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${formatNumber(stats.totalRevenue || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Growth Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.growthRate || 0)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Cycle Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Subscriptions</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Count</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {formatNumber(stats.monthlySubscriptions || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Revenue</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  ${formatNumber(stats.monthlyRevenue || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Yearly Subscriptions</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Count</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {formatNumber(stats.yearlySubscriptions || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600">Revenue</span>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  ${formatNumber(stats.yearlyRevenue || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cancelled</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.cancelledSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Expired</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.expiredSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Past Due</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.pastDueSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Trial</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats.trialSubscriptions || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">All Subscriptions</h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Next Billing</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td>
                      <div 
                        onClick={() => navigate(`/users/${subscription.id}`)}
                        className="cursor-pointer hover:text-blue-600 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900 hover:text-blue-600">
                          {subscription.user.name}
                        </div>
                        <div className="text-sm text-gray-500 hover:text-blue-500">
                          {subscription.user.email}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`text-sm font-medium ${getPlanColor(subscription.plan)}`}>
                        {capitalize(subscription.plan)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(subscription.status)}
                        <span className={`badge ${getStatusBadgeClass(subscription.status)}`}>
                          {capitalize(subscription.status)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-gray-900">
                        ${subscription.amount}
                        <span className="text-xs text-gray-500 ml-1">
                          /{subscription.plan === 'free' ? 'free' : `${subscription?.billingCycle || ' '}`}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-900">
                        {subscription.plan === 'free' ? 'N/A' : formatDate(subscription.nextBilling)}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-900">
                        {formatDate(subscription.createdAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.currentPage - 1) * 20) + 1} to {Math.min(pagination.currentPage * 20, pagination.totalSubscriptions)} of {pagination.totalSubscriptions} subscriptions
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                fetchSubscriptions(newPage);
              }}
              disabled={!pagination.hasPrevPage}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                fetchSubscriptions(newPage);
              }}
              disabled={!pagination.hasNextPage}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;